import {
  enumCurrencyCode,
  enumUnit,
  MoneyInput,
  Unit,
} from '@rematter/frontend-lib-api-client';

import {
  BrokeredOrderValidationError,
  ItemMarginRecord,
} from '~/modules/brokerage/types/brokeredOrder';
import { BrokeredOrderLineItemRecord } from '~/modules/brokerage/types/brokeredOrderLineItem';

export const ZERO_MONEY: MoneyInput = {
  amount: 0,
  currencyCode: enumCurrencyCode.USD,
};

export const computeLineItemTotals = (item: {
  purchaseUnitPrice: MoneyInput | null;
  salesUnitPrice: MoneyInput | null;
  quantity: number;
}) => {
  const purchaseAmount = (item.purchaseUnitPrice?.amount ?? 0) * item.quantity;
  const salesAmount = (item.salesUnitPrice?.amount ?? 0) * item.quantity;
  const currencyCode =
    item.purchaseUnitPrice?.currencyCode ?? enumCurrencyCode.USD;
  const marginAmount = salesAmount - purchaseAmount;

  return {
    purchaseTotal: { amount: purchaseAmount, currencyCode },
    salesTotal: { amount: salesAmount, currencyCode },
    margin: { amount: marginAmount, currencyCode },
    marginPercent: salesAmount > 0 ? (marginAmount / salesAmount) * 100 : 0,
  };
};

export const createMarginsMap = (
  marginsData: ItemMarginRecord[],
): Map<string, ItemMarginRecord> =>
  new Map(marginsData.map((m) => [m.itemId, m]));

export const getMarginAutoFillForItem = (
  itemId: string,
  marginsMap: Map<string, ItemMarginRecord> | undefined,
): Partial<{
  unit: Unit;
  purchaseUnitPrice: MoneyInput;
  salesUnitPrice: MoneyInput;
}> => {
  const marginData = marginsMap?.get(itemId);
  if (!marginData) return {};

  return {
    ...(marginData.unit && { unit: marginData.unit }),
    purchaseUnitPrice: marginData.purchaseUnitPrice ?? ZERO_MONEY,
    salesUnitPrice: marginData.salesUnitPrice ?? ZERO_MONEY,
  };
};

export const createEmptyLineItem = (): BrokeredOrderLineItemRecord => ({
  id: `temp-${Date.now()}-${Math.random()}`,
  itemId: '',
  item: null,
  quantity: 0,
  unit: enumUnit.Pounds,
  purchaseUnitPrice: ZERO_MONEY,
  salesUnitPrice: ZERO_MONEY,
  purchaseTotal: ZERO_MONEY,
  salesTotal: ZERO_MONEY,
  margin: ZERO_MONEY,
  marginPercent: 0,
  notes: null,
});

export const computeHasUnaddedOrderItems = (
  lineItems: BrokeredOrderLineItemRecord[],
  marginsMap: Map<string, ItemMarginRecord>,
): boolean => {
  if (marginsMap.size === 0) return false;
  const existingItemIds = new Set(lineItems.map((li) => li.itemId));
  return [...marginsMap.keys()].some((itemId) => !existingItemIds.has(itemId));
};

type ComputeValidationProps = {
  lineItems: BrokeredOrderLineItemRecord[];
  marginsMap: Map<string, ItemMarginRecord>;
  hasLinkedOrders: boolean;
  serverValidationErrors: BrokeredOrderValidationError[] | undefined;
  translations: {
    ITEM_NOT_IN_SALES_ORDERS: string;
    ITEM_NOT_IN_PURCHASE_ORDERS: string;
    INSUFFICIENT_PO_QUANTITY: string;
  };
};

export const computeValidationErrorsByItemId = ({
  lineItems,
  marginsMap,
  hasLinkedOrders,
  serverValidationErrors,
  translations,
}: ComputeValidationProps): Map<
  string,
  BrokeredOrderValidationError[]
> | null => {
  const errorMap = new Map<string, BrokeredOrderValidationError[]>();

  const addError = (itemId: string, error: BrokeredOrderValidationError) => {
    const existing = errorMap.get(itemId) ?? [];
    existing.push(error);
    errorMap.set(itemId, existing);
  };

  if (hasLinkedOrders) {
    lineItems
      .filter((li) => li.itemId)
      .forEach((lineItem) => {
        const { itemId } = lineItem;
        const marginData = marginsMap.get(itemId);

        if (!marginData) {
          return addError(itemId, {
            code: 'ITEM_NOT_IN_PURCHASE_ORDERS',
            message: translations.ITEM_NOT_IN_PURCHASE_ORDERS,
            itemId,
          });
        }

        if (
          lineItem.quantity > marginData.quantity &&
          marginData.quantity > 0
        ) {
          addError(itemId, {
            code: 'INSUFFICIENT_PO_QUANTITY',
            message: translations.INSUFFICIENT_PO_QUANTITY,
            itemId,
          });
        }
      });
  }

  serverValidationErrors
    ?.filter((e) => e.itemId)
    .forEach((error) => {
      const itemId = error.itemId ?? '';
      const existing = errorMap.get(itemId) ?? [];
      const isDuplicate = existing.some((e) => e.code === error.code);
      if (!isDuplicate) addError(itemId, error);
    });

  return errorMap.size > 0 ? errorMap : null;
};

export const toLineItemCreateInput = (
  items: BrokeredOrderLineItemRecord[],
): Array<{
  itemId: string;
  quantity: number;
  unit: Unit;
  notes?: string | null;
}> =>
  items
    .filter((li) => li.itemId && li.quantity > 0)
    .map((li) => ({
      itemId: li.itemId,
      quantity: li.quantity,
      unit: li.unit,
      notes: li.notes,
    }));
